import React, { useEffect, useState } from "react";
import { axInst } from "../../../Components/Login/Service";
import { useQuery } from "@tanstack/react-query";
import WaterMark from "../../../Components/WaterMark/WaterMark";
import { IoMdArrowDropdown } from "react-icons/io";
import { TbHandClick } from "react-icons/tb";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Loader } from "rsuite";
import { useSelector } from "react-redux";

const region_table_details = async (body) => {
  try {
    const res = await axInst.post("offtake/region-wise-offtake", body);
    const obj = res.data;

    const sum = Object.values(obj).reduce(
      (sum, e) => ({
        today_sum: sum.today_sum + e.today,
        mtd_sum: sum.mtd_sum + e.mtd,
        total_sum: sum.total_sum + e.total,
      }),
      { today_sum: 0, mtd_sum: 0, total_sum: 0 }
    );

    const formattedData = Object.keys(obj).map((region) => ({
      Region: region,
      Today: obj[region].today,
      MTD: obj[region].mtd,
      Total: obj[region].total,
      States: [],
    }));

    return {
      obj: formattedData,
      today_sum: sum.today_sum,
      mtd_sum: sum.mtd_sum,
      total_sum: sum.total_sum,
    };
  } catch (error) {
    console.error(error);
    return {
      obj: [],
      today_sum: 0,
      mtd_sum: 0,
      total_sum: 0,
    };
  }
};

const state_table_details = async (requestBody) => {
  try {
    const res = await axInst.post("offtake/state-wise-offtake", requestBody);
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const RegionWise = () => {
  const { data: filterdata, submitted } = useSelector(
    (state) => state.filtersOfftake
  );

  const { data, isFetching, isError } = useQuery({
    queryKey: ["regionTable", submitted],
    queryFn: () => region_table_details(filterdata),
    initialData: {
      obj: [],
      today_sum: 0,
      mtd_sum: 0,
      total_sum: 0,
    },
    refetchInterval: 300000,
    refetchIntervalInBackground: true,
    cacheTime: 0,
    staleTime: 0,
  });

  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    return () => {
      setExpandedRows({});
    };
  }, []);

  const toggleRowExpanded = async (region, filterdata) => {
    try {
      if (!expandedRows[region]) {
        // ✅ merge filterdata and add region
        const requestBody = {
          ...filterdata,
          region: [region], // override/add region
        };

        const stateData = await state_table_details(requestBody);

        setExpandedRows((prev) => ({
          ...prev,
          [region]: stateData,
        }));
      } else {
        setExpandedRows((prev) => ({
          ...prev,
          [region]: !prev[region] ? expandedRows[region] : false,
        }));
      }
    } catch (error) {
      console.error("Error toggling row expansion:", error);
    }
  };

  const columns = [
    {
      id: "region",
      header: "Region",
      accessorFn: (value) => value.Region,
      cell: (info) => (
        <span className="text-left" title="Click to view state wise offtake">
          {info?.getValue()}
        </span>
      ),
      className: "text-left",
    },
    {
      header: "Today's Offtake",
      accessorFn: (value) => value.Today,
      cell: ({ row }) => (
        <span className="text-right">{row?.original.Today}</span>
      ),
      className: "text-right",
    },
    {
      header: "MTD Offtake",
      accessorFn: (value) => value.MTD,
      cell: ({ row }) => (
        <span className="text-right">{row?.original.MTD}</span>
      ),
      className: "text-right",
    },
    {
      header: "Total",
      accessorFn: (value) => value.Total,
      cell: ({ row }) => (
        <span className="text-right">{row?.original.Total}</span>
      ),
      className: "text-right",
    },
  ];

  const table = useReactTable({
    data: data?.obj ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  return (
    <div className="p-2 relative">
      <header className="pb-2 text-base font-semibold text-[#4c4c4c]">
        Region and State Wise Offtake
      </header>
      <div className="self-center overflow-auto h-[15.7rem] ">
        {isError && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Error Occurred</p>
          </div>
        )}
        {isFetching && (
          <div className="grid items-center justify-center w-full h-full">
            <Loader />
          </div>
        )}
        {!isFetching && !isError && !data && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Data Not Available</p>
          </div>
        )}
        {!isFetching && !isError && data && (
          <>
            <WaterMark />
            <table className="w-full">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="p-1 text-sm lg:text-base border text-center border-gray-300"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`even:bg-gray-200 odd:bg-white ${
                        rowIndex === 0 ? "cursor-default" : "cursor-pointer"
                      }`}
                      onClick={() =>
                        row.original.Region !== "All Regions" &&
                        toggleRowExpanded(row.original.Region, filterdata)
                      }
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <td
                          key={cell.id}
                          className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8 relative"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                          {cellIndex === 0 &&
                            row.original.Region !== "All Regions" && (
                              <IoMdArrowDropdown className="h-3 w-3 text-gray-400 ml-1 inline-block " />
                            )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows[row.original.Region] &&
                      expandedRows[row.original.Region] !== false &&
                      Object.entries(expandedRows[row.original.Region]).map(
                        ([state, data], index) => (
                          <tr key={index} className="bg-[#2A5775] text-white">
                            <td className="p-1 text-center text-xs lg:text-sm border border-gray-300 h-8">
                              {state}
                            </td>
                            <td className="p-1 text-center text-xs lg:text-sm border border-gray-300 h-8">
                              {data.today}
                            </td>
                            <td className="p-1 text-center text-xs lg:text-sm border border-gray-300 h-8">
                              {data.mtd}
                            </td>
                            <td className="p-1 text-center text-xs lg:text-sm border border-gray-300 h-8">
                              {data.total}
                            </td>
                          </tr>
                        )
                      )}
                  </React.Fragment>
                ))}
              </tbody>

              <tfoot className="sticky bottom-0 bg-[#2A5775] text-white">
                <tr>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    Grand Total
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.today_sum}
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.mtd_sum}
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.total_sum}
                  </td>
                </tr>
              </tfoot>
            </table>
            <p className="text-xs mt-2 flex items-center">
              <TbHandClick /> on Region to view States Offtake
            </p>
          </>
        )}
      </div>
    </div>
  );
};
